import React, { useState } from 'react';
import axios from 'axios';
import { KeyRound, Phone, Loader2, CheckCircle2, AlertTriangle, ArrowRight } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const h = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

export default function MTProtoAuthModal({ open, onOpenChange, onAuthSuccess }) {
  const [step, setStep] = useState(1); // 1 = phone, 2 = otp, 3 = done
  const [phone, setPhone] = useState('+60');
  const [code, setCode] = useState('');
  const [password, setPassword] = useState(''); // 2FA password (optional)
  const [needs2fa, setNeeds2fa] = useState(false);
  const [loading, setLoading] = useState(false);
  const [me, setMe] = useState(null);

  const reset = () => {
    setStep(1);
    setPhone('+60');
    setCode('');
    setPassword('');
    setNeeds2fa(false);
    setMe(null);
  };

  const sendCode = async () => {
    if (!phone.trim().startsWith('+')) {
      toast.error('Phone mesti start dengan + dan country code (cth +60123456789)');
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/mtproto/send-code`, { phone: phone.trim() }, h());
      if (res.data.success) {
        toast.success(`OTP dihantar ke ${phone}`);
        setStep(2);
      } else {
        toast.error(res.data.error || 'Failed to send code');
      }
    } catch (e) {
      toast.error(e.response?.data?.error || e.response?.data?.detail || 'Send code failed');
    } finally {
      setLoading(false);
    }
  };

  const verifyCode = async () => {
    if (!code.trim()) {
      toast.error('Sila masukkan OTP code');
      return;
    }
    setLoading(true);
    try {
      const body = { code: code.trim() };
      if (password) body.password = password;
      const res = await axios.post(`${API}/mtproto/verify-code`, body, h());
      if (res.data.success) {
        setMe({ name: res.data.first_name || res.data.username, username: res.data.username });
        setStep(3);
        toast.success('MTProto authenticated!');
        if (onAuthSuccess) onAuthSuccess();
      } else if (res.data.needs_2fa || (res.data.error || '').toLowerCase().includes('password')) {
        setNeeds2fa(true);
        toast.warning('Akaun ada 2FA. Sila masukkan password Telegram anda.');
      } else {
        toast.error(res.data.error || 'Verify failed');
      }
    } catch (e) {
      const err = e.response?.data?.error || e.response?.data?.detail || '';
      if (err.toLowerCase().includes('password') || err.toLowerCase().includes('two-step')) {
        setNeeds2fa(true);
        toast.warning('Sila masukkan password 2FA Telegram');
      } else {
        toast.error(err || 'Verify failed');
      }
    } finally {
      setLoading(false);
    }
  };

  const close = () => {
    onOpenChange(false);
    setTimeout(reset, 400);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md bg-[hsl(var(--card))] border-[hsl(var(--border))] text-white" data-testid="mtproto-auth-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2.5 text-base font-bold">
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-violet-500 to-purple-700 flex items-center justify-center">
              <KeyRound size={17} className="text-white" />
            </div>
            MTProto Re-Authentication
          </DialogTitle>
        </DialogHeader>

        {/* Steps indicator */}
        <div className="flex items-center gap-2 mb-2">
          {[1, 2, 3].map(s => (
            <div key={s} className="flex items-center gap-2 flex-1">
              <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold ${step >= s ? 'bg-violet-500 text-white' : 'bg-[hsl(var(--surface))] text-[hsl(var(--text-muted))]'}`}>
                {step > s ? <CheckCircle2 size={12} /> : s}
              </div>
              <span className={`text-[10px] font-semibold uppercase tracking-wider ${step >= s ? 'text-white' : 'text-[hsl(var(--text-muted))]'}`}>
                {s === 1 ? 'Phone' : s === 2 ? 'OTP' : 'Done'}
              </span>
              {s < 3 && <div className={`flex-1 h-px ${step > s ? 'bg-violet-500' : 'bg-[hsl(var(--border))]'}`} />}
            </div>
          ))}
        </div>

        {step === 1 && (
          <div className="space-y-4">
            <div className="rounded-lg bg-amber-500/10 border border-amber-500/30 p-3 flex gap-2 text-[11px]">
              <AlertTriangle size={14} className="text-amber-400 shrink-0 mt-0.5" />
              <div className="text-amber-200/80 leading-relaxed">
                Phone mesti yang sama yang digunakan sebelum ni (anda ada admin access kepada channel-channel tu).
                Pastikan Telegram apps di phone anda buka — OTP akan masuk dalam <b>Telegram chat</b>, bukan SMS.
              </div>
            </div>
            <div>
              <label className="text-[10px] font-bold uppercase tracking-wider text-[hsl(var(--text-muted))] mb-1.5 block">Phone Number</label>
              <div className="relative">
                <Phone size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[hsl(var(--text-muted))]" />
                <Input value={phone} onChange={e => setPhone(e.target.value)} placeholder="+60193793455" className="pl-9 h-10" data-testid="phone-input" />
              </div>
              <p className="text-[10px] text-[hsl(var(--text-muted))] mt-1.5">Format: +countrycode then number (no spaces, no dashes)</p>
            </div>
            <Button onClick={sendCode} disabled={loading || phone.length < 8} className="w-full bg-violet-600 hover:bg-violet-700 text-white" data-testid="send-code-btn">
              {loading ? <Loader2 size={14} className="animate-spin mr-2" /> : <ArrowRight size={14} className="mr-2" />}
              Send OTP
            </Button>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4">
            <div className="rounded-lg bg-blue-500/10 border border-blue-500/30 p-3 text-[11px] text-blue-200/80 leading-relaxed">
              OTP dihantar ke akaun Telegram untuk <b>{phone}</b>.<br />
              Buka Telegram app — anda akan terima mesej dari <b>Telegram</b> (official) dengan kod 5 digit.
            </div>
            <div>
              <label className="text-[10px] font-bold uppercase tracking-wider text-[hsl(var(--text-muted))] mb-1.5 block">OTP Code</label>
              <Input value={code} onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="12345" inputMode="numeric" maxLength={6}
                className="h-12 text-center text-2xl font-mono tracking-[0.3em]" data-testid="otp-input" />
            </div>
            {needs2fa && (
              <div>
                <label className="text-[10px] font-bold uppercase tracking-wider text-[hsl(var(--text-muted))] mb-1.5 block">2FA Password</label>
                <Input type="password" value={password} onChange={e => setPassword(e.target.value)}
                  placeholder="Telegram 2FA password" className="h-10" data-testid="2fa-input" />
                <p className="text-[10px] text-[hsl(var(--text-muted))] mt-1.5">Akaun anda ada Two-Step Verification. Masukkan password Telegram.</p>
              </div>
            )}
            <div className="flex gap-2">
              <Button onClick={() => setStep(1)} variant="outline" className="flex-1" disabled={loading}>
                Back
              </Button>
              <Button onClick={verifyCode} disabled={loading || code.length < 4} className="flex-1 bg-emerald-600 hover:bg-emerald-700" data-testid="verify-btn">
                {loading ? <Loader2 size={14} className="animate-spin mr-2" /> : <CheckCircle2 size={14} className="mr-2" />}
                Verify
              </Button>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-4 text-center py-4">
            <div className="w-16 h-16 mx-auto rounded-full bg-emerald-500/15 flex items-center justify-center">
              <CheckCircle2 size={36} className="text-emerald-400" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-white">Authenticated!</h3>
              <p className="text-xs text-[hsl(var(--text-muted))] mt-1">
                Logged in as <span className="text-white font-semibold">{me?.name || me?.username || phone}</span>
              </p>
            </div>
            <div className="rounded-lg bg-emerald-500/10 border border-emerald-500/30 p-3 text-[11px] text-emerald-200/80 text-left leading-relaxed">
              ✓ MTProto session sekarang ACTIVE<br />
              ✓ Session disimpan dalam database (akan survive container restart)<br />
              ✓ Auto-reconnect akan kekal aktif<br />
              <br />
              <b>Next step:</b> Klik close → balik ke Admin Panel → tekan <b>Sync All</b> untuk pull data terbaru semua channel.
            </div>
            <Button onClick={close} className="w-full bg-violet-600 hover:bg-violet-700" data-testid="done-btn">
              Done
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
